import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { Typography, Container, TextField, Box, Button, Avatar } from '@mui/material';
import { auth, db } from '../../firebase/firebase';
import { addEmployee, addEmployeeWorkHours, fetchAllEmployees, fetchEmployeeSignUpInvite, getCurrentBusinessConfig, updateEmployeeScheduleForDate, uploadImageToStorage, generateRecurringBreakBlockersForEmployee } from '../../firebase/firebase-delegate';
import { generateScheduleBitString, extractTimesFromBitString } from '../../time/timeUtils';
import { WorkHours, Employee, BitStringData, BusinessConfig, RecurringBreak } from '../../types';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { BUFFER_PERIOD, DAYJS_DAYS_OF_WEEK } from '../../constants';
import ImageSelect from '../../components/global/images/ImageSelector';
import ScheduleCard from '../../components/employee/services/ScheduleCard';
import { useMutation, useQuery } from '@tanstack/react-query';
import ErrorDialog from '../../components/global/ErrorDialog';
import { callSetWebsiteClaim } from '../../firebase/cloud-function-helper';
import { DB_WEBSITE_REF, EMPLOYEES_DB_REF } from '../../firebase/firebase-references';
import { dayjs } from '../../utils/dayjs-config';
import LoadingSpinner from '../../components/global/LoadingSpinner';
import { validateForm } from '../../components/booking/ConfirmAppointment';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { hasDuplicateEmployeeEmail, normalizeEmail } from '../../utils/emailUtils';
import { convertBusinessHoursToWorkHours } from '../../utils/businessHours';

const SignUpPage: React.FC = () => {
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [admin, setAdmin] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [inviteCode, setInviteCode] = useState<string | null>(null);
    const [inviteData, setInviteData] = useState<any | null>(null);
    const [recurringBreakTemplates, setRecurringBreakTemplates] = useState<Record<keyof WorkHours, RecurringBreak[]>>(() =>
        DAYJS_DAYS_OF_WEEK.reduce((acc, day) => {
            acc[day] = [];
            return acc;
        }, {} as Record<keyof WorkHours, RecurringBreak[]>)
    );
    const [profilePicChangePlaceHolder, setProfilePicChangePlaceHolder] = useState<File | undefined>(undefined);
    const templateEndDate = dayjs().add(3, 'month');

    const flattenRecurringBreaks = () =>
        DAYJS_DAYS_OF_WEEK.flatMap((day, index) =>
            (recurringBreakTemplates[day] || []).map((definition) => ({
                ...definition,
                weekday: index,
            }))
        );

    // Error messages
    const [errors, setErrors] = useState<{ firstName?: string; lastName?: string; email?: string; phone?: string; }>({});

    const location = useLocation();
    const navigate = useNavigate();

    const businessConfigQuery = useQuery<BusinessConfig | null>({
        queryKey: ['businessConfig'],
        queryFn: () => getCurrentBusinessConfig(),
    });
    const defaultWorkHours = useMemo(
        () => convertBusinessHoursToWorkHours(businessConfigQuery.data?.businessInfo?.businessHours),
        [businessConfigQuery.data?.businessInfo?.businessHours]
    );
    const [schedules, setSchedules] = useState<WorkHours>(defaultWorkHours);
    const [didHydrateBusinessHourDefaults, setDidHydrateBusinessHourDefaults] = useState(false);

    // Fetch the current employee data
    const allEmployeesQuery = useQuery<Array<Employee>>({
        queryKey: ['allEmployees'],
        queryFn: () => fetchAllEmployees(),
    });

    useEffect(() => {
        if (didHydrateBusinessHourDefaults) {
            return;
        }
        if (!businessConfigQuery.data?.businessInfo?.businessHours) {
            return;
        }
        setSchedules(defaultWorkHours);
        setDidHydrateBusinessHourDefaults(true);
    }, [businessConfigQuery.data?.businessInfo?.businessHours, defaultWorkHours, didHydrateBusinessHourDefaults]);

    useEffect(() => {
        verifyInviteCodeMutation.mutateAsync();
    }, [location]);

    const verifyInviteCodeMutation = useMutation({
        mutationFn: async () => {
            const urlParams = new URLSearchParams(location.search);
            const code = urlParams.get('code');

            if (!code) {
                throw new Error('Cannot sign up: Missing verification code.'); // 🔴 Ensure this throws
            }

            return await fetchEmployeeSignUpInvite(code);
        },
        onSuccess: (invitation) => {
            setAdmin(invitation.isAdmin || false);
            setInviteCode(new URLSearchParams(location.search).get('code') || '');
            setInviteData(invitation);
        },
        onError: (err) => {
            console.log("there was an error!")
            console.error('Failed to verify invite:', err);
            // setErrorMessage('Error verifying invite code: ' + (err as Error).message);
            setShowErrorDialog(true);
        },
    });

    const addEmployeeMutation = useMutation({
        mutationFn: async () => {
            if (!inviteCode || !inviteData) {
                throw new Error('Invalid invite code.');
            }

            // 🔁 Refetch allEmployees to get the most up-to-date count
            const updatedEmployeesSnapshot = await allEmployeesQuery.refetch();
            const employees = updatedEmployeesSnapshot.data || [];
            const loginEnabledEmployees = employees.filter((employee) => employee.isOrchestrated !== true);

            const employeeLimit = businessConfigQuery.data?.features?.employeeCount || 1;
            if (loginEnabledEmployees.length >= employeeLimit) {
                setErrorMessage("Cannot add employee: your current plan limit has been reached.");
                throw new Error("Employee limit exceeded");
            }

            const normalizedEmail = normalizeEmail(email);

            // Do not let them add an email that already exists
            const emailExists = hasDuplicateEmployeeEmail(employees, normalizedEmail);
            if (emailExists) {
                setErrorMessage("An employee exists with that email. Please use a different email.");
                throw Error("An employee exists with that email. Please use a different email.");
            }

            const userCredential = await createUserWithEmailAndPassword(auth, normalizedEmail, password);
            const user = userCredential.user;
            await callSetWebsiteClaim(user.uid, inviteCode);
            await user.getIdTokenResult(true);

            const flattenedBreaks = flattenRecurringBreaks();
            const newEmployee: Employee = {
                id: user.uid,
                admin,
                firstName,
                lastName,
                email: normalizedEmail,
                phone,
                appointments: {},
                recurringBreaks: flattenedBreaks,
                active: true,
            };

            if (profilePicChangePlaceHolder) {
                newEmployee.profilePictureImagePath = `${EMPLOYEES_DB_REF}${newEmployee.id}/profilePicture`;
                await uploadImageToStorage(profilePicChangePlaceHolder, newEmployee.profilePictureImagePath, /*compressionLevel=*/3);
            }

            await addEmployee(newEmployee);
            // ✅ Set owner field if it's not already set
            const paymentInfoRef = doc(db, DB_WEBSITE_REF, 'config', 'paymentInfo');
            const paymentInfoSnap = await getDoc(paymentInfoRef);
            if (!paymentInfoSnap.exists() || !paymentInfoSnap.data()?.owner || paymentInfoSnap.data()?.owner === '') {
                const preferencesRef = doc(db, DB_WEBSITE_REF, 'config', 'preferences');
                const preferencesSnap = await getDoc(preferencesRef);
                const isOrchestratorMode =
                    preferencesSnap.data()?.orchestratorMode === true ||
                    businessConfigQuery.data?.preferences?.orchestratorMode === true;

                const ownerUpdatePromise = setDoc(paymentInfoRef, {
                    owner: newEmployee.id,
                    lastUpdated: Date.now(),
                }, { merge: true });

                if (isOrchestratorMode) {
                    await Promise.all([
                        ownerUpdatePromise,
                        setDoc(preferencesRef, {
                            primaryOrchestratorId: newEmployee.id,
                        }, { merge: true }),
                    ]);
                } else {
                    await ownerUpdatePromise;
                }
            }
            businessConfigQuery.refetch();
            await addEmployeeWorkHours(newEmployee.id, schedules);

            const missingDates = Array.from({ length: BUFFER_PERIOD * 2 + 1 }, (_, i) =>
                dayjs().add(i, 'day')
            );

            await Promise.all(
                missingDates.map(missingDate =>
                    updateEmployeeScheduleForDate(newEmployee.id, schedules, dayjs(missingDate, 'MMDDYYYY'))
                )
            );

            const windowStart = dayjs();
            const windowEnd = dayjs().add(BUFFER_PERIOD * 2, 'day');
            await generateRecurringBreakBlockersForEmployee({
                employeeId: newEmployee.id,
                windowStart,
                windowEnd,
                recurringBreakIdsToReset: flattenedBreaks.map((definition) => definition.id),
            });

            const serviceIds: string[] = inviteData.services || [];

            // Update each service document to include the new employee ID
            await Promise.all(serviceIds.map(async (serviceId) => {
                const serviceRef = doc(db, DB_WEBSITE_REF, 'services', serviceId);
                const serviceSnap = await getDoc(serviceRef);

                if (serviceSnap.exists()) {
                    const serviceData = serviceSnap.data();
                    const updatedEmployees = [...(serviceData.employees || []), newEmployee.id];

                    await updateDoc(serviceRef, { employees: updatedEmployees });
                }
            }));

            // Consider deleting the invite document if necessary
            // await deleteDoc(doc(db, 'invites', inviteCode));

            return newEmployee;
        },
        onSuccess: () => {
            // If the name is "", it means they have not done the config
            if (admin && businessConfigQuery.data?.businessInfo.name === '') {
                navigate('/admin/business-config');
            }
            navigate('/admin');
        },
        onError: (err) => {
            console.log('Error completing sign-up:', err.message);
            setErrorMessage('Error completing sign-up. ' + err.message);
            setShowErrorDialog(true);
        },
        onSettled: () => {
            setFormLoading(false);
        },
    });


    const isLoading =
        addEmployeeMutation.isPending ||
        verifyInviteCodeMutation.isPending ||
        businessConfigQuery.isLoading ||
        allEmployeesQuery.isLoading;

    const isError =
        verifyInviteCodeMutation.isError ||
        businessConfigQuery.isError ||
        allEmployeesQuery.isError;

    const employeeLimit = businessConfigQuery.data?.features?.employeeCount || 1;
    const currentEmployeeCount = (allEmployeesQuery.data ?? []).filter((employee) => employee.isOrchestrated !== true).length;
    const isAtLimit = !!employeeLimit && currentEmployeeCount >= employeeLimit;

    const handleScheduleChange = (day: keyof WorkHours, updatedSchedule: BitStringData) => {
        setSchedules((prevSchedules) => ({
            ...prevSchedules,
            [day]: generateScheduleBitString(updatedSchedule),
        }));
    };

    const handleRecurringBreaksChange = (day: keyof WorkHours, breaks: RecurringBreak[]) => {
        setRecurringBreakTemplates((prev) => ({
            ...prev,
            [day]: breaks,
        }));
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setFormLoading(true);

        const trimmedFirstName = firstName.trim();
        const trimmedLastName = lastName.trim();
        const trimmedEmail = email.trim();
        const trimmedPhone = phone.trim();

        const newErrors = validateForm(trimmedFirstName, trimmedLastName, trimmedEmail, trimmedPhone);
        setErrors(newErrors);

        if (Object.keys(newErrors).length > 0) {
            setFormLoading(false);
            return;
        }

        addEmployeeMutation.mutate();
    };


    const onImageChange = (imageFile?: File) => {
        setProfilePicChangePlaceHolder(imageFile);
    };

    if (isLoading) return <LoadingSpinner />;

    if (isError) {
        return (
            <Container maxWidth="sm">
                <Typography variant="h4" align="center" sx={{ mt: 4 }} gutterBottom>
                    Something Went Wrong
                </Typography>
                <Typography color="error" align="center" sx={{ mt: 2 }}>
                    There was an error verifying the invite or loading setup data.
                </Typography>
            </Container>
        );
    }

    if (isAtLimit) {
        return (
            <Container maxWidth="sm">
                <Typography variant="h4" align="center" sx={{ mt: 4 }} gutterBottom>
                    Employee Limit Reached
                </Typography>
                <Typography align="center" sx={{ mt: 2 }}>
                    You cannot sign up because this business has reached its team member limit.
                    <br />
                    Please contact the <strong>Scheduify</strong> team to upgrade the website package.
                </Typography>
            </Container>
        );
    }
    return (
        <Container maxWidth="sm">
            <Typography variant="h5" align="center" fontWeight="bold" gutterBottom sx={{ mt: 4, mb: 2 }}>
                SIGN UP
            </Typography>
            <hr
                style={{
                    border: "none",
                    height: "2px",
                    backgroundColor: "rgba(0,0,0,0.2)",
                    boxShadow: "0 1px 5px rgba(0, 0, 0, 0.3)",
                    marginBottom: "20px",
                }}
            />
            <form onSubmit={(handleSubmit)}>
                <Box display="flex" flexDirection="column" alignItems="center">
                    <Typography
                        variant="overline"
                        align="center"
                        display="block"
                    >
                        ENTER YOUR DETAILS
                    </Typography>

                    <ImageSelect
                        onImageChange={onImageChange}
                        placeholderComponent={<Avatar sx={{ width: 100, height: 100 }} />}
                        altText="Profile Image"
                    />
                    <TextField
                        label="First Name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        required
                        error={Boolean(errors.firstName)}
                        helperText={errors.firstName}
                        sx={{ width: '80%', mt: 1, mb: 2 }}
                    />
                    <TextField
                        label="Last Name"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        required
                        error={Boolean(errors.lastName)}
                        helperText={errors.lastName}
                        sx={{ width: '80%', mb: 2 }}
                    />
                    <style>{`
                    .PhoneInput {
                        width: 80% !important;
                    }
                    .PhoneInputInput {
                        height: 56px !important;
                        padding: 10.5px 14px !important;
                        font-size: 16px !important;
                        border-radius: 4px !important;
                        border: 1px solid rgba(0, 0, 0, 0.23) !important;
                        box-sizing: border-box !important;
                        margin: 0 auto !important;
                        margin-bottom: 16px !important;
                    }
                    `}</style>
                    <PhoneInput
                        defaultCountry="US"
                        countries={['US', 'CA']}
                        international={true}
                        addInternationalOption={false}
                        value={phone}
                        onChange={(value) => setPhone(value || '')}
                        className="PhoneInput"
                        inputStyle={{
                            border: 'none',
                            outline: 'none',
                            width: '100%',
                            fontSize: '16px',
                            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                        }}
                    />
                    {Boolean(errors.phone) && (
                        <>
                            <style>{`
                            .PhoneInputInput {
                                border: 1px solid #ff1744 !important;
                                margin-bottom: 0 !important;
                            }
                            `}</style>
                            <div style={{ color: '#ff1744', fontSize: '0.75rem', marginTop: 4, marginLeft: 14, marginBottom: 14, width: '80%' }}>
                                {errors.phone}
                            </div>
                        </>
                    )}
                    <TextField
                        label="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        error={Boolean(errors.email)}
                        helperText={errors.email}
                        required
                        sx={{ width: '80%', mb: 2 }}
                    />
                    <TextField
                        label="Password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        sx={{ width: '80%', mb: 2 }}
                    />
                </Box>
                <Box sx={{
                    p: 2,
                    mx: 'auto',
                }} >
                    <Typography
                        variant="overline"
                        align="center"
                        display="block"
                        sx={{ mb: 2 }}
                    >
                        SET YOUR SCHEDULE
                    </Typography>
                    {(Object.keys(schedules) as (keyof WorkHours)[]).map((day) => {
                        const parsed = extractTimesFromBitString(schedules[day]);
                        return (
                            <ScheduleCard
                                key={day}
                                day={day}
                                initialWorkHours={parsed}
                                onScheduleChange={handleScheduleChange}
                            recurringBreaks={recurringBreakTemplates[day]}
                                onRecurringBreaksChange={handleRecurringBreaksChange}
                                templateEndDate={templateEndDate}
                            />
                        );
                    })}

                    <Button type="submit" variant="contained" color="primary" disabled={formLoading} sx={{ width: '100%', mt: 2, mb: 2 }}>
                        {formLoading ? 'Loading...' : 'Sign Up'}
                    </Button>
                </Box>
            </form>
            <ErrorDialog open={showErrorDialog} onClose={() => { setShowErrorDialog(false) }} message={errorMessage} />
        </Container>
    );
};

export default SignUpPage;
